/**
 * Password rules for Create Member / Edit Account.
 * Callers paint the field red/green only once the operator has typed —
 * never on an empty field.
 */

const MIN_LENGTH = 9
const MAX_LENGTH = 60

function countMatches(password: string, pattern: RegExp): number {
  return password.match(pattern)?.length ?? 0
}

/** aaa, abc, or cba runs of three — the continuous-character ban. */
export function hasContinuousCharacters(password: string): boolean {
  if (!password || password.length < 3) return false
  for (let i = 0; i < password.length - 2; i++) {
    const c1 = password.charCodeAt(i)
    const c2 = password.charCodeAt(i + 1)
    const c3 = password.charCodeAt(i + 2)
    if (c1 === c2 && c2 === c3) return true
    if (c2 === c1 + 1 && c3 === c2 + 1) return true
    if (c2 === c1 - 1 && c3 === c2 - 1) return true
  }
  return false
}

export interface PasswordCheck {
  key: string
  label: string
  passed: boolean
}

export function passwordChecks(password: string): PasswordCheck[] {
  const value = password || ''
  return [
    {
      key: 'length',
      label: '9–60 characters',
      passed: value.length >= MIN_LENGTH && value.length <= MAX_LENGTH,
    },
    {
      key: 'uppercase',
      label: 'At least 1 uppercase letter',
      passed: /[A-Z]/.test(value),
    },
    {
      key: 'lowercase',
      label: 'At least 1 lowercase letter',
      passed: /[a-z]/.test(value),
    },
    {
      key: 'numbers',
      label: 'At least 1 number',
      passed: countMatches(value, /[0-9]/g) >= 1,
    },
    {
      key: 'special',
      label: 'At least 1 special character',
      passed: countMatches(value, /[^A-Za-z0-9]/g) >= 1,
    },
    {
      key: 'continuous',
      label: 'No continuous characters',
      passed: !value || !hasContinuousCharacters(value),
    },
  ]
}

export function isPasswordValid(password: string): boolean {
  if (!password) return false
  return passwordChecks(password).every((check) => check.passed)
}

export const PASSWORD_POLICY_MESSAGE =
  'Password must be 9–60 characters with at least 1 uppercase, 1 lowercase, 1 number, 1 special character, and no continuous characters.'

export function passwordError(password: string): string | null {
  if (password.trim() === '') return 'Enter a password for the new account'
  return isPasswordValid(password) ? null : PASSWORD_POLICY_MESSAGE
}

/** Edit Member: blank keeps the existing password; typed values still need the policy. */
export function passwordErrorOptional(password: string): string | null {
  if (password.trim() === '') return null
  return isPasswordValid(password) ? null : PASSWORD_POLICY_MESSAGE
}

export function confirmPasswordError(password: string, confirm: string): string | null {
  if (confirm.trim() === '') return 'Confirm the password'
  if (password !== confirm) return "Confirm password doesn't match"
  return null
}

/** Edit Member: confirm only required when a new password was typed. */
export function confirmPasswordErrorOptional(password: string, confirm: string): string | null {
  if (password.trim() === '' && confirm.trim() === '') return null
  if (password.trim() === '') return confirm.trim() === '' ? null : "Confirm password doesn't match"
  return confirmPasswordError(password, confirm)
}
